import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled, { css, useTheme } from 'styled-components'
import contextToiTeaser from '@contexts/toiTeaser'
import contextViewport from '@contexts/viewport'
import { ComputedTeaserFlex } from '@widgets/Teaser/types'
import { NewstickerHorizontalProps } from '@widgets/Teaser/toi/types'
import NewstickerPost from './NewstickerPost'
import { ViewportTypeValues } from '@contexts/viewport'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import TextLine from '../../TextLine'
import SvgIcon from '@components/SvgIcon'
import { layout, desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

const { TOITeaserLayoutContext, TOITeaserNewstickerContext } = contextToiTeaser
const { ViewportTypeContext } = contextViewport

const {
  desktop: { minWidth: minWidthDesktop },
  tablet: { minWidth: minWidthTablet },
} = layout

interface StyledNewstickerHorizontalProps {
  itemsContained: number
}

const StyledNewstickerHorizontal = styled.div<StyledNewstickerHorizontalProps>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 85px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.8);

  ${mobileCSS(css`
    display: none;
  `)}
`

const StyledNewstickerHorizontalInnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledScrollableNewstickerHorizontal = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: ${computeTeaserWidth({
    flexValue: '6',
    viewportType: ViewportTypeValues.DESKTOP,
  })}px;
`
const StyledScroll = styled.button<{
  teaserFlex: ComputedTeaserFlex
  scrollDirection: 'left' | 'right'
  hide: boolean
}>`
  ${({ teaserFlex, scrollDirection, hide }) => {
    const teaserFlexDesktop = teaserFlex['desktop']

    return css`
      ${desktopCSS(css`
        ${!hide && teaserFlexDesktop !== '6'
          ? css`
          position: absolute;
          height: 100%;
          top: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-color: transparent;
          padding: 0;
          border: 0;
          padding-${scrollDirection === 'left' ? 'right' : 'left'}: 20px;
          ${scrollDirection}: 0;
          cursor: pointer;
        `
          : css`
              display: none;
            `};
      `)}

      ${tabletCSS(css`
        display: none;
      `)}

      ${mobileCSS(css`
        display: none;
      `)}
    `
  }};
`

const StyledSvgIcon = styled(SvgIcon)`
  ${({
    theme: {
      colors: { white },
    },
  }) => css`
    display: block;
    background-color: ${white};
  `}
`

const NewstickerHorizontal: FunctionComponent<NewstickerHorizontalProps> = ({
  clickableProps,
}) => {
  const [position, setPosition] = useState<number>(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const viewportType = useContext(ViewportTypeContext)
  const { teaserFlex, isInMegaModule } = useContext(TOITeaserLayoutContext)
  const { posts } = useContext(TOITeaserNewstickerContext)
  const maxItems = posts.length
  const teaserWidth = computeTeaserWidth({
    flexValue: teaserFlex[viewportType],
    viewportType:
      viewportType === ViewportTypeValues.DESKTOP
        ? ViewportTypeValues.DESKTOP
        : ViewportTypeValues.TABLET,
  })
  const theme = useTheme()

  const cornerPadding = Math.round(
    (1.6 / 100) *
      (viewportType === ViewportTypeValues.DESKTOP
        ? minWidthDesktop
        : minWidthTablet)
  )

  const newstickerViewportArea = teaserWidth - cornerPadding - 8

  useEffect(() => {
    setPosition(0)
  }, [posts])

  useEffect(() => {
    if (scrollContainerRef.current) {
      if (position === maxItems - 1) {
        scrollContainerRef.current.scrollLeft =
          position * 300 - (newstickerViewportArea - 300)
      } else {
        scrollContainerRef.current.scrollLeft = position * 300
      }
    }
  }, [position, newstickerViewportArea, maxItems])

  return (
    <StyledNewstickerHorizontal itemsContained={maxItems}>
      <StyledNewstickerHorizontalInnerWrapper ref={scrollContainerRef}>
        <StyledScrollableNewstickerHorizontal>
          {posts.map(({ id, dateInfo, text }, index) => (
            <NewstickerPost
              key={id}
              dateInfo={dateInfo}
              isFirstChild={index === 0}
              isLastChild={index === maxItems - 1}
              clickableProps={clickableProps}>
              {text}
            </NewstickerPost>
          ))}
        </StyledScrollableNewstickerHorizontal>
        <StyledScroll
          aria-label="scroll left"
          scrollDirection="left"
          teaserFlex={teaserFlex}
          hide={position === 0}
          onClick={(e) => {
            e.stopPropagation()
            if (position > 0) {
              setPosition((currentPosition) => currentPosition - 1)
            }
          }}>
          <StyledSvgIcon
            iconName="gallery-previous"
            size={24}
            color={theme.colors.grey.grey_100}
          />
        </StyledScroll>
        <StyledScroll
          aria-label="scroll right"
          scrollDirection="right"
          teaserFlex={teaserFlex}
          hide={position === maxItems - 1}
          onClick={(e) => {
            e.stopPropagation()
            if (position < maxItems - 1) {
              setPosition((currentPosition) => currentPosition + 1)
            }
          }}>
          <StyledSvgIcon
            iconName="gallery-next"
            size={24}
            color={theme.colors.grey.grey_100}
          />
        </StyledScroll>
      </StyledNewstickerHorizontalInnerWrapper>
      {isInMegaModule ? <TextLine isInNewsticker={true} /> : null}
    </StyledNewstickerHorizontal>
  )
}

export default NewstickerHorizontal
